import { ClaireService } from '../../services/claireService';
import { WAKE_CHUNK, WakeWordDetector, type OrtLike } from './wakeWord';

interface Recognizer {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  abort(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

const recognizerCtor = (): (new () => Recognizer) | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: new () => Recognizer; webkitSpeechRecognition?: new () => Recognizer };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

/** The browser's speech recognition hears "Hey Claire"; browsers without it fall back to the stock on-device model. */
export const WAKE_PHRASE = 'Hey Claire';
const FALLBACK_WAKE_PHRASE = 'Hey Jarvis';
export const END_PHRASE = 'Thanks Claire, you’re good';
const WAKE_MODEL = '/wakeword/hey_jarvis_v0.1.onnx';
const WAKE_THRESHOLD = 0.5;
const NAME = '(?:claire|clare|clair|clara|cleo)';
const WAKE_RE = new RegExp(`\\b(?:hey|hi|okay|ok)[\\s,.!]+${NAME}\\b`, 'i');
const END_RE = new RegExp(`\\b(?:thanks|thank you)\\b[\\s,.!]*${NAME}\\b[\\s,.!]*(?:you['’]?re|you are|your)\\s+(?:good|all set|done)\\b`, 'i');

const ORT_BASE = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.20.1/dist/';
const RATE = 16000;
const CHUNK_MS = (WAKE_CHUNK / RATE) * 1000;
/** Speech recognition reports the wake phrase a second or two late, so keep ~3 s of audio before it. */
const PRE_ROLL_CHUNKS = 38;
const LEAD_CHUNKS = 3;
/** Extra pause allowed right after the wake phrase before the question counts as finished. */
const WAKE_GRACE_MS = 1500;
const END_SILENCE_MS = 900;
const NO_SPEECH_MS = 5000;
/** In a conversation, Claire waits this long for the next thing said before going back to waiting for "Hey Claire". */
const CONVERSATION_IDLE_MS = 120_000;
const MAX_QUESTION_MS = 15000;
const MIN_SPEECH_RMS = 350;

export type HandsFreeState = 'off' | 'starting' | 'standby' | 'waiting' | 'paused' | 'listening' | 'thinking' | 'speaking';

export interface HandsFreeStatus {
  state: HandsFreeState;
  heard?: string;
  answer?: string;
  notice?: string;
  /** Between "Hey Claire" and "Thanks Claire, you're good". */
  conversing?: boolean;
  wakePhrase?: string;
}

export interface HandsFreeOptions {
  getSessionId: () => string;
  shared: () => boolean;
  onStatus: (s: HandsFreeStatus) => void;
}

let ortPromise: Promise<OrtLike> | null = null;
function loadOrt(): Promise<OrtLike> {
  ortPromise ??= new Promise<OrtLike>((resolve, reject) => {
    const w = window as unknown as { ort?: OrtLike & { env: { wasm: Record<string, unknown> } } };
    const ready = () => {
      w.ort!.env.wasm.wasmPaths = ORT_BASE;
      w.ort!.env.wasm.numThreads = 1;
      resolve(w.ort!);
    };
    if (w.ort) return ready();
    const s = document.createElement('script');
    s.src = `${ORT_BASE}ort.min.js`;
    s.crossOrigin = 'anonymous';
    s.onload = ready;
    s.onerror = () => {
      ortPromise = null;
      reject(new Error('Could not load the wake-word engine. Check the internet connection.'));
    };
    document.head.appendChild(s);
  });
  return ortPromise;
}

const TAP_WORKLET = `
class ClaireTap extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(2048); this.n = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) {
      for (let i = 0; i < ch.length; i++) {
        this.buf[this.n++] = ch[i];
        if (this.n === this.buf.length) { this.port.postMessage(this.buf, [this.buf.buffer]); this.buf = new Float32Array(2048); this.n = 0; }
      }
    }
    return true;
  }
}
registerProcessor('claire-tap', ClaireTap);
`;

/** Linear resampler to 16 kHz on the int16 scale, emitting fixed 1280-sample chunks. */
class Chunker {
  private out = new Float32Array(WAKE_CHUNK);
  private n = 0;
  private pos = 0;
  private readonly step: number;
  constructor(inputRate: number, private readonly emit: (chunk: Float32Array) => void) {
    this.step = inputRate / RATE;
  }
  push(input: Float32Array) {
    while (this.pos < input.length) {
      const i = Math.floor(this.pos);
      const frac = this.pos - i;
      const a = input[i];
      const b = i + 1 < input.length ? input[i + 1] : a;
      this.out[this.n++] = Math.max(-1, Math.min(1, a + (b - a) * frac)) * 32767;
      if (this.n === WAKE_CHUNK) {
        this.emit(this.out);
        this.out = new Float32Array(WAKE_CHUNK);
        this.n = 0;
      }
      this.pos += this.step;
    }
    this.pos -= input.length;
  }
}

const rms = (c: Float32Array) => {
  let s = 0;
  for (let i = 0; i < c.length; i++) s += c[i] * c[i];
  return Math.sqrt(s / c.length);
};

function wavBase64(chunks: Float32Array[]): string {
  const samples = chunks.reduce((n, c) => n + c.length, 0);
  const buf = new ArrayBuffer(44 + samples * 2);
  const v = new DataView(buf);
  const str = (o: number, s: string) => [...s].forEach((ch, i) => v.setUint8(o + i, ch.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples * 2, true);
  str(8, 'WAVEfmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, RATE, true);
  v.setUint32(28, RATE * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, samples * 2, true);
  let o = 44;
  for (const c of chunks) for (let i = 0; i < c.length; i++, o += 2) v.setInt16(o, Math.max(-32768, Math.min(32767, Math.round(c[i]))), true);
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/**
 * "Hey Claire" for FIN. Listens on-device for the wake word, records the question that follows,
 * and plays Claire's answer. Only one browser tab listens at a time.
 */
export class HandsFreeEngine {
  private status: HandsFreeStatus = { state: 'off' };
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private detector: WakeWordDetector | null = null;
  private queue: Promise<void> = Promise.resolve();
  private releaseLock: (() => void) | null = null;
  private recent: Float32Array[] = [];
  private recording: Float32Array[] = [];
  private heardSpeech = false;
  private silentMs = 0;
  private listenMs = 0;
  private noiseFloor = MIN_SPEECH_RMS / 2.5;
  private cooldownUntil = 0;
  private audio: HTMLAudioElement | null = null;
  private stopped = false;
  private conversing = false;
  private idleLimitMs = NO_SPEECH_MS;
  private recognizer: Recognizer | null = null;
  /** Highest recognizer result already acted on, so one phrase triggers once. */
  private handledResult = -1;
  private wakePhrase = recognizerCtor() ? WAKE_PHRASE : FALLBACK_WAKE_PHRASE;
  private resumeOnGesture = () => {
    if (this.ctx?.state === 'suspended') void this.ctx.resume();
  };

  constructor(private readonly opts: HandsFreeOptions) {}

  get state(): HandsFreeState {
    return this.status.state;
  }

  async start(): Promise<void> {
    this.stopped = false;
    this.set({ state: 'starting' });
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    if (!locks) return this.run();
    let granted = false;
    setTimeout(() => !granted && !this.stopped && this.set({ state: 'standby', notice: 'Listening in another FIN tab.' }), 400);
    void locks
      .request('claire-hands-free', () => {
        granted = true;
        if (this.stopped) return undefined;
        return new Promise<void>((resolve) => {
          this.releaseLock = resolve;
          void this.run();
        });
      })
      .catch(() => undefined);
  }

  stop(): void {
    this.stopped = true;
    this.conversing = false;
    const rec = this.recognizer;
    this.recognizer = null;
    rec?.abort();
    this.audio?.pause();
    this.audio = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    document.removeEventListener('pointerdown', this.resumeOnGesture);
    document.removeEventListener('keydown', this.resumeOnGesture);
    this.releaseLock?.();
    this.releaseLock = null;
    this.set({ state: 'off' });
  }

  /** Stops Claire mid-answer; a conversation stays open. */
  hush(): void {
    if (this.status.state !== 'speaking') return;
    this.audio?.pause();
    this.audio = null;
    this.afterTurn({ heard: this.status.heard, answer: this.status.answer });
  }

  private set(s: HandsFreeStatus) {
    this.status = { ...s, conversing: this.conversing, wakePhrase: this.wakePhrase };
    this.opts.onStatus(this.status);
  }

  /** Watches what the browser hears for "Hey Claire" and "Thanks Claire, you're good". Returns false if unavailable. */
  private startRecognizer(): boolean {
    const Ctor = recognizerCtor();
    if (!Ctor) return false;
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    let restartMs = 250;
    rec.onresult = (e) => {
      restartMs = 250;
      for (let i = Math.max(e.resultIndex, this.handledResult + 1); i < e.results.length; i++) {
        if (this.onPhrase(e.results[i][0]?.transcript || '')) this.handledResult = i;
      }
    };
    rec.onerror = (e) => {
      if (this.recognizer !== rec) return;
      if (e.error === 'not-allowed') return this.fail('FIN needs microphone permission. Allow it in the address bar, then turn hands-free on again.');
      if (e.error === 'service-not-allowed' || e.error === 'language-not-supported') {
        this.recognizer = null;
        void this.useWakeModel();
        return;
      }
      if (e.error === 'network') restartMs = Math.min(restartMs * 4, 15000);
    };
    rec.onend = () => {
      if (this.recognizer !== rec || this.stopped) return;
      this.handledResult = -1;
      setTimeout(() => {
        if (this.recognizer !== rec || this.stopped) return;
        try {
          rec.start();
        } catch {
          /* already started */
        }
      }, restartMs);
    };
    this.recognizer = rec;
    this.handledResult = -1;
    try {
      rec.start();
    } catch {
      this.recognizer = null;
      return false;
    }
    return true;
  }

  /** Browsers whose speech recognition is unavailable listen for the stock wake word on-device instead. */
  private async useWakeModel(): Promise<void> {
    try {
      const ort = await loadOrt();
      this.detector ??= await WakeWordDetector.create(ort, {
        melspectrogram: '/wakeword/melspectrogram.onnx',
        embedding: '/wakeword/embedding_model.onnx',
        wakeword: WAKE_MODEL,
      });
      if (this.stopped) return;
      this.detector.reset();
      this.wakePhrase = FALLBACK_WAKE_PHRASE;
      if (this.status.state === 'waiting') this.set({ state: 'waiting', notice: `This browser can’t hear “${WAKE_PHRASE}”. Say “${FALLBACK_WAKE_PHRASE}” instead.` });
    } catch (e: unknown) {
      this.fail((e as { message?: string })?.message || 'Hands-free could not start.');
    }
  }

  private onPhrase(text: string): boolean {
    if (this.stopped) return false;
    const state = this.status.state;
    if (this.conversing && END_RE.test(text)) {
      this.endConversation();
      return true;
    }
    if (WAKE_RE.test(text) && (state === 'waiting' || state === 'speaking') && Date.now() >= this.cooldownUntil) {
      this.onWake(true);
      return true;
    }
    return false;
  }

  private async run(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      if (this.stopped) return void stream.getTracks().forEach((t) => t.stop());
      this.stream = stream;
      if (!this.startRecognizer()) await this.useWakeModel();
      if (this.stopped) return;

      let ctx: AudioContext;
      try {
        ctx = new AudioContext({ sampleRate: RATE });
      } catch {
        ctx = new AudioContext();
      }
      this.ctx = ctx;
      const url = URL.createObjectURL(new Blob([TAP_WORKLET], { type: 'application/javascript' }));
      await ctx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      const tap = new AudioWorkletNode(ctx, 'claire-tap', { numberOfInputs: 1, numberOfOutputs: 1 });
      const mute = ctx.createGain();
      mute.gain.value = 0;
      ctx.createMediaStreamSource(stream).connect(tap).connect(mute).connect(ctx.destination);
      const chunker = new Chunker(ctx.sampleRate, (c) => {
        this.queue = this.queue.then(() => this.onChunk(c)).catch(() => undefined);
      });
      tap.port.onmessage = (e: MessageEvent<Float32Array>) => chunker.push(e.data);

      document.addEventListener('pointerdown', this.resumeOnGesture);
      document.addEventListener('keydown', this.resumeOnGesture);
      ctx.onstatechange = () => {
        if (this.stopped) return;
        if (ctx.state === 'suspended') this.set({ state: 'paused', notice: 'Click anywhere in FIN to resume hands-free.' });
        else if (ctx.state === 'running' && this.status.state === 'paused') this.set({ state: 'waiting' });
      };
      this.detector?.reset();
      this.set(ctx.state === 'running' ? { state: 'waiting' } : { state: 'paused', notice: 'Click anywhere in FIN to resume hands-free.' });
      stream.getAudioTracks()[0]?.addEventListener('ended', () => !this.stopped && this.fail('The microphone was disconnected.'));
    } catch (e: unknown) {
      const err = e as { name?: string; message?: string };
      this.fail(
        err?.name === 'NotAllowedError'
          ? 'FIN needs microphone permission. Allow it in the address bar, then turn hands-free on again.'
          : err?.name === 'NotFoundError'
            ? 'No microphone found on this computer.'
            : err?.message || 'Hands-free could not start.',
      );
    }
  }

  private fail(notice: string) {
    this.stop();
    this.set({ state: 'off', notice });
  }

  private async onChunk(chunk: Float32Array): Promise<void> {
    if (this.stopped) return;
    this.recent.push(chunk);
    if (this.recent.length > PRE_ROLL_CHUNKS) this.recent.shift();
    const level = rms(chunk);
    const state = this.status.state;

    if (state === 'listening') return this.onQuestionAudio(chunk, level);
    if (state !== 'waiting' && state !== 'speaking') return;

    if (state === 'waiting' && level < this.noiseFloor * 2) this.noiseFloor = this.noiseFloor * 0.97 + level * 0.03;
    if (!this.detector || this.recognizer) return;
    const score = await this.detector.process(chunk);
    if (score >= WAKE_THRESHOLD && Date.now() >= this.cooldownUntil) this.onWake(false);
  }

  /** `heardByRecognizer`: the wake phrase is already in the pre-roll, so the person is mid-sentence. */
  private onWake(heardByRecognizer: boolean) {
    this.cooldownUntil = Date.now() + 1500;
    this.audio?.pause();
    this.audio = null;
    this.detector?.reset();
    this.conversing = true;
    this.recording = heardByRecognizer ? [...this.recent] : this.recent.slice(-LEAD_CHUNKS);
    this.heardSpeech = heardByRecognizer;
    this.silentMs = heardByRecognizer ? -WAKE_GRACE_MS : 0;
    this.listenMs = 0;
    this.idleLimitMs = NO_SPEECH_MS;
    this.chime();
    this.set({ state: 'listening' });
  }

  /** Next turn of a conversation: listen again without the wake phrase. */
  private listenAgain(keep: Pick<HandsFreeStatus, 'heard' | 'answer'> = {}) {
    this.recording = this.recent.slice(-LEAD_CHUNKS);
    this.heardSpeech = false;
    this.silentMs = 0;
    this.listenMs = 0;
    this.idleLimitMs = CONVERSATION_IDLE_MS;
    this.set({ ...keep, state: 'listening' });
  }

  private afterTurn(keep: Pick<HandsFreeStatus, 'heard' | 'answer' | 'notice'> = {}) {
    if (this.stopped) return;
    if (this.conversing) this.listenAgain(keep);
    else this.set({ ...keep, state: 'waiting' });
  }

  private endConversation(notice = `Okay. Say “${this.wakePhrase}” when you need me.`) {
    this.conversing = false;
    this.audio?.pause();
    this.audio = null;
    this.recording = [];
    this.detector?.reset();
    this.cooldownUntil = Date.now() + 1500;
    this.chime(true);
    this.set({ state: 'waiting', notice });
  }

  private onQuestionAudio(chunk: Float32Array, level: number) {
    this.recording.push(chunk);
    this.listenMs += CHUNK_MS;
    if (level > Math.max(MIN_SPEECH_RMS, this.noiseFloor * 2.5)) {
      this.heardSpeech = true;
      this.silentMs = 0;
    } else {
      this.silentMs += CHUNK_MS;
    }
    if (!this.heardSpeech) {
      if (this.recording.length > LEAD_CHUNKS) this.recording.shift();
      if (this.listenMs >= this.idleLimitMs) {
        if (this.idleLimitMs === CONVERSATION_IDLE_MS) this.endConversation(`I’ll stop listening for now. Say “${this.wakePhrase}” when you need me.`);
        else {
          this.conversing = false;
          this.set({ state: 'waiting', notice: 'I didn’t catch a question.' });
        }
      }
      return;
    }
    if ((this.heardSpeech && this.silentMs >= END_SILENCE_MS) || this.listenMs >= MAX_QUESTION_MS) {
      void this.ask(this.recording);
      this.recording = [];
    }
  }

  private async ask(chunks: Float32Array[]) {
    this.set({ state: 'thinking' });
    try {
      const reply = await ClaireService.handsFree({
        audio: wavBase64(chunks),
        mimeType: 'audio/wav',
        sessionId: this.opts.getSessionId(),
        shared: this.opts.shared(),
      });
      if (this.stopped || this.status.state !== 'thinking') return;
      if (reply.ended) return this.endConversation();
      if (!reply.heard) return this.conversing ? this.listenAgain() : this.set({ state: 'waiting', notice: 'I didn’t catch that.' });
      if (!reply.audio || !reply.contentType) return this.afterTurn({ heard: reply.heard, answer: reply.text });
      this.detector?.reset();
      const audio = new Audio(`data:${reply.contentType};base64,${reply.audio}`);
      this.audio = audio;
      const done = () => {
        if (this.audio !== audio) return;
        this.audio = null;
        if (this.status.state === 'speaking') this.afterTurn({ heard: this.status.heard, answer: this.status.answer });
      };
      audio.onended = done;
      audio.onerror = done;
      this.set({ state: 'speaking', heard: reply.heard, answer: reply.text });
      await audio.play().catch(done);
    } catch (e: unknown) {
      if (this.stopped || this.status.state !== 'thinking') return;
      const msg = (e as { message?: string })?.message?.replace(/^.*?:\s*/, '');
      this.afterTurn({ notice: msg || 'Claire could not be reached.' });
    }
  }

  /** Rising tone when Claire starts listening; falling when a conversation ends. */
  private chime(falling = false) {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(falling ? 880 : 660, t);
    osc.frequency.linearRampToValueAtTime(falling ? 520 : 990, t + 0.12);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.24);
  }
}
